// CardPage.tsx
import { FC } from "react";
import { ChevronRight } from "lucide-react";
import { Link, NavLink, useParams } from "react-router-dom";
import data from "@/pages/resources/articles.json";
import GettingStarted from "../landing/common/GettingStarted";
import Footer from "../landing/layout/Footer";
import Header from "../landing/layout/Header";

// Types matching your JSON object
interface ArticleContent {
  id: string;
  content: string;
}
interface Article {
  id: string | number;
  image: string;
  heading: string;
  subHeading?: string;
  articles?: ArticleContent[];
  topic: string;
}

/**
 * Prefix each utility class with `tw-`.
 * Handles responsive / state prefixes: e.g. md:text-4xl -> md:tw-text-4xl
 */
function prefixTailwindClasses(classString: string): string {
  return classString
    .trim()
    .split(/\s+/)
    .map((cls) => {
      if (!cls) return "";
      if (cls.startsWith("tw-")) return cls; // already prefixed
      // handle variants like md:text-4xl, hover:shadow-lg, sm:hover:xyz
      const parts = cls.split(":");
      if (parts.length > 1) {
        const prefix = parts.slice(0, parts.length - 1).join(":");
        const base = parts[parts.length - 1];
        return `${prefix}:tw-${base}`;
      }
      return `tw-${cls}`;
    })
    .join(" ");
}

/**
 * Convert className='...' to class="..." and prefix classes with tw-
 * Returns transformed HTML string
 */
function transformHtmlFromJson(rawHtml: string): string {
  if (!rawHtml) return rawHtml;
  // 1) convert className='...' or className="..." to class="..."
  const step1 = rawHtml.replace(/className\s*=\s*(['"])(.*?)\1/gi, (_, __, inner) => {
    // inner are the classes inside quotes
    const prefixed = prefixTailwindClasses(inner);
    // use double quotes for consistency
    return `class="${prefixed}"`;
  });

  // 2) convert any remaining class='...' or class="..." (just in case) to prefixed classes
  const step2 = step1.replace(/class\s*=\s*"(.*?)"/gi, (_, classInner) => {
    const prefixed = prefixTailwindClasses(classInner);
    return `class="${prefixed}"`;
  });

  return step2;
}

const CardPage: FC = () => {
  const { id } = useParams<{ id?: string }>();

  const articles = data as Article[];
  const article = articles.find((a) => String(a.id) === id);

  if (!article) {
    return <div className="tw-text-center tw-text-red-500">Article not found</div>;
  }

  return (
    <>
      <Header />
      <div id="article-Page" className="lg:tw-mx-10 tw-pt-28 tw-text-white tw-relative tw-rounded-3xl tw-bg-[#0C0C0C]">
        {/* Category Label */}
        <span className="md:tw-mx-20 tw-mx-10 tw-inline-block tw-text-xs tw-py-2 tw-px-6 tw-rounded-sm tw-mb-4 tw-bg-[#003D4E]">
          {article.topic}
        </span>

        {/* Main Title */}
        <h1 className="md:tw-text-5xl tw-mx-10 md:tw-mx-20 tw-font-semibold tw-w-3/5" data-aos="fade-up">
          {article.heading}
        </h1>

        <div className="tw-grid md:tw-grid-cols-3 tw-mx-10 md:tw-mx-20 md:tw-gap-10 tw-mt-10" >
          {/* Left Content */}
          <div className="tw-col-span-2 tw-text-lg tw-flex tw-flex-col tw-gap-12">
            <img
              data-aos="fade-up"
              src={article.image}
              alt={article.heading}
              className="tw-w-full tw-h-[360px] tw-rounded-3xl tw-object-cover"
            />

            {/* Render JSON HTML paragraphs (converted) */}
            <div id="article-content" className="tw-space-y-6">
              {article.articles?.map((para) => (
                <div data-aos="fade-up"
                  key={para.id}
                  // transform content: className -> class and add tw- prefixes
                  dangerouslySetInnerHTML={{ __html: transformHtmlFromJson(para.content) }}
                />
              ))}
            </div>
            <NavLink to="/articles" className="tw-flex lg:tw-hidden tw-items-center tw-my-10 tw-gap-2 tw-text-blue-400 hover:tw-text-blue-600">
              Back to Home <ChevronRight />
            </NavLink>
          </div>

          {/* Right Sidebar */}
          <div className="tw-col-span-1 tw-hidden lg:tw-flex tw-flex-col tw-sticky tw-gap-6">
            {article.subHeading && (
              <h2 data-aos="fade-up" className="tw-text-2xl tw-font-semibold tw-text-[#F4F4F4]">
                {article.subHeading}
              </h2>
            )}

            <NavLink to="/articles" className="tw-flex tw-items-center tw-gap-2 tw-text-blue-400 hover:tw-text-blue-600">
              Back to Home <ChevronRight />
            </NavLink>

            {/* Other articles preview */}
            {articles
              .filter((a) => String(a.id) !== String(article.id))
              .slice(0, 3)
              .map((s) => (
                <div data-aos="fade-up" key={String(s.id)} className="tw-w-full tw-bg-[#1A1A1A] tw-rounded-xl tw-overflow-hidden">
                  <img src={s.image} alt={s.heading} className="tw-h-30 tw-w-full tw-object-cover" />
                  <div className="tw-flex tw-flex-col tw-h-60 tw-justify-between tw-p-4">
                    <h2 className="tw-text-lg tw-font-semibold">{s.heading}</h2>
                    <NavLink to={`/articles/${s.id}`} className="tw-flex tw-items-center tw-text-[#00C5FB]">
                      Learn More <ChevronRight />
                    </NavLink>
                  </div>
                </div>
              ))}

            <div data-aos="fade-up" className="tw-sticky tw-top-25 tw-bg-[#00C5FB] tw-text-black tw-font-semibold tw-rounded-xl tw-flex tw-flex-col tw-gap-8 tw-px-10 tw-py-6">
              <span className="tw-text-lg">
                Interested in learning how Protega can support your business?
              </span>
              <Link to={'/schedule-demo'} className="tw-bg-white tw-p-4 tw-text-2xl tw-rounded-xl">Schedule a Demo</Link>
            </div>
          </div>
        </div>

        {/* Read More */}
        <section className="tw-pt-10 tw-hidden lg:tw-flex tw-flex-col tw-rounded-b-3xl  tw-overflow-hidden tw-gap-0">
          <h1 className="md:tw-text-[10rem] read tw-mx-20 tw-font-bold tw-text-base tw-text-[#606060]">Read more</h1>

          <div className="tw-pl-20 tw-flex tw-flex-wrap tw-gap-5 tw-pt-10 tw-bg-[#606060] tw-pb-45">
            {articles
              .filter(
                (item) =>
                  String(item.id) !== String(article.id) &&
                  !articles
                    .filter((s) => String(s.id) !== String(article.id))
                    .slice(0, 3)
                    .some((sidebarItem) => String(sidebarItem.id) === String(item.id))
              )
              .slice(0, 3)
              .map((readMoreItem) => (
                <div key={String(readMoreItem.id)} className="tw-overflow-hidden  tw-w-[380px] tw-rounded-xl tw-bg-black tw-h-[420px]">
                  <div className="tw-overflow-hidden tw-px-14 tw-justify-end tw-bg-[#222222] tw-items-center tw-flex tw-h-[45%] tw-w-full tw-relative">
                    <img
                      src={readMoreItem.image}
                      alt={readMoreItem.heading}
                      className="tw-border-2 tw-border-[#00C5FB] tw-rounded-full tw-w-[250px] tw-h-[250px] tw-object-cover tw-absolute -tw-left-[30%] -tw-top-[20%]"
                    />
                    <h1 className="tw-w-3/5 tw-py-2 tw-border-[#00C5FB] tw-border-b-2">
                      {readMoreItem.subHeading}
                    </h1>
                  </div>

                  <div className="tw-p-10 tw-flex tw-flex-col tw-justify-between tw-h-[60%]">
                    <h1 className="tw-text-xl tw-font-semibold tw-mb-10">{readMoreItem.heading}</h1>
                    <NavLink to={`/articles/${readMoreItem.id}`} className="tw-text-sm tw-flex tw-items-center tw-text-[#00C5FB]">
                      Learn More <ChevronRight />
                    </NavLink>
                  </div>
                </div>
              ))}
          </div>
        </section>
      </div>
      <GettingStarted />

      <section className="tw-bg-[#00C5FB] tw-mt-20 md:tw-p-12 tw-p-8 tw-text-center">
        <h1 className="md:tw-text-4xl tw-font-semibold">
          Interested in learning how Protega can <br />
          support your business?
        </h1>
        <button className="tw-px-10 tw-py-3 tw-bg-white tw-text-black tw-text-lg tw-rounded-lg tw-mt-10 tw-font-semibold">
          Schedule a Demo
        </button>
      </section>
      <Footer />
    </>
  );
};

export default CardPage;
